/**
 * gm-accordion
 *
 * Accordion group controller shared by the Gilbert Maxwell FAQ sections
 * (gm-faq-home, gm-faq-page). One element per accordion group.
 *
 * Markup contract, per group:
 *   <gm-accordion>
 *     <div data-gm-accordion-item>
 *       <h3><button type="button" data-gm-accordion-trigger
 *            id="TRIGGER_ID" aria-expanded="false" aria-controls="PANEL_ID">...</button></h3>
 *       <div data-gm-accordion-panel id="PANEL_ID" role="region" aria-labelledby="TRIGGER_ID">
 *         <div>...answer...</div>
 *       </div>
 *     </div>
 *   </gm-accordion>
 *
 * Behavior: real buttons, aria-expanded kept in sync, single item open per
 * group. The element toggles the is-open class on the item; the section's
 * stylesheet animates the panel with grid-template-rows from 0fr to 1fr and
 * collapses every transition to instant under prefers-reduced-motion.
 */

class GmAccordion extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    if (this.#controller) this.#controller.abort();
    this.#controller = null;
  }

  /**
   * Triggers belonging to this group only (guards against nested groups).
   * @returns {Element[]}
   */
  get #triggers() {
    const all = this.querySelectorAll('[data-gm-accordion-trigger]');
    return Array.from(all).filter((trigger) => trigger.closest('gm-accordion') === this);
  }

  /**
   * Resolves the trigger a click or key event happened on, if any.
   * @param {Event} event
   * @returns {Element | null}
   */
  #triggerFrom(event) {
    if (!(event.target instanceof Element)) return null;

    const trigger = event.target.closest('[data-gm-accordion-trigger]');
    if (!trigger || trigger.closest('gm-accordion') !== this) return null;

    return trigger;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const trigger = this.#triggerFrom(event);
    if (!trigger) return;

    this.#toggle(trigger);
  };

  /**
   * Optional roving focus between question buttons within the group.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const trigger = this.#triggerFrom(event);
    if (!trigger) return;

    const triggers = this.#triggers;
    const index = triggers.indexOf(trigger);
    if (index === -1 || triggers.length === 0) return;

    let target = null;

    switch (event.key) {
      case 'ArrowDown':
        target = triggers[(index + 1) % triggers.length];
        break;
      case 'ArrowUp':
        target = triggers[(index - 1 + triggers.length) % triggers.length];
        break;
      case 'Home':
        target = triggers[0];
        break;
      case 'End':
        target = triggers[triggers.length - 1];
        break;
      default:
        return;
    }

    if (target instanceof HTMLElement) {
      event.preventDefault();
      target.focus();
    }
  };

  /** @param {Element} trigger */
  #toggle(trigger) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.#setState(trigger, false);
      return;
    }

    for (const other of this.#triggers) {
      if (other !== trigger && other.getAttribute('aria-expanded') === 'true') {
        this.#setState(other, false);
      }
    }

    this.#setState(trigger, true);
  }

  /**
   * @param {Element} trigger
   * @param {boolean} open
   */
  #setState(trigger, open) {
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

    const item = trigger.closest('[data-gm-accordion-item]');
    if (item) item.classList.toggle('is-open', open);
  }
}

if (!customElements.get('gm-accordion')) {
  customElements.define('gm-accordion', GmAccordion);
}
